import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import type { ApiClientFilter } from "@/features/admin/api/dto";
import { useApiClients } from "@/features/admin/api/use-api-clients";
import {
  ApiClientFormDialog,
  apiClientsColumns,
  DeviceSessionKeyExchangeCard,
  type ApiClientRow,
} from "@/features/admin/components";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";

/** Traduit les filtres de colonnes DataTableClar en `ApiClientFilter` (§4.2). */
const toApiClientFilter = (filters: ColumnFiltersState): ApiClientFilter => {
  const filter: ApiClientFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "name") filter.name = value;
    if (id === "status")
      filter.status = value as ApiClientFilter["status"];
  }
  return filter;
};

/**
 * Clients API partenaires (`POST /ApiClients/all` — endpoints PROPRES AU
 * GATEWAY, sans préfixe `/gw/`, guide §6.1). Création (apiKey affichée une
 * seule fois), révocation, édition des scopes. Rôles : flashpay-superadmin,
 * flashpay-admin. Bas de page : ⚙️ échange de clé de session device.
 */
export function ApiClientsPage() {
  const { t } = useAdminTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toApiClientFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useApiClients(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-foreground">
            {t("admin.apiClients.title")}
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t("admin.apiClients.subtitle")}
          </p>
        </div>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("admin.apiClients.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is ApiClientRow =>
          Boolean(r.id)
        )}
        columnsClar={apiClientsColumns}
        title={t("admin.apiClients.title")}
        exportFileName="api-clients"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <DeviceSessionKeyExchangeCard />

      <ApiClientFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
